import { useEffect, useState } from "react";
import axios from '../api/axiosInstance';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

function AdminWeightHistory({ open, user, onClose, onUpdated }) {
  const [records, setRecords] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const token = localStorage.getItem("token");
  const [displayUnit, setDisplayUnit] = useState("kg"); // 'kg' | 'lb' | 'jin'
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() =>
    new Date().toISOString().split("T")[0]
  );
  const handleUnitChange = (e) => {
    setDisplayUnit(e.target.value);
  };

  const fetchRecords = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`/api/Weight?userid=${user.userId}&startDate=${startDate}&endDate=${endDate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(res.data);
    } catch {
      alert("查詢失敗");
    }
  };

  const handleEdit = (r) => {
    const kg = parseFloat(r.weightInKg);
    setEditingId(r.id);
    setEditData({
      recordedAt: r.recordedAt?.slice(0, 16) || "",
      weightInKg: kg.toString(),
      pound: (kg * 2.20462).toFixed(1),
      jin: (kg * 2).toFixed(1),
      notes: r.notes || "",
    });
  };

  const handleSave = async (id) => {
    try {
      await axios.put(
        `/api/Admin/weight/${id}`,
        { ...editData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingId(null);
      fetchRecords();
      if (onUpdated) onUpdated();
    } catch {
      alert("更新失敗");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("確定要刪除？")) return;
    try {
      await axios.delete(`/api/Admin/weight/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchRecords();
      if (onUpdated) onUpdated();
    } catch {
      alert("刪除失敗");
    }
  };

  const convert = (kg, unit) => {
    if (unit === "kg") return kg;
    if (unit === "lb") return (kg * 2.20462).toFixed(1);
    if (unit === "jin") return (kg * 2).toFixed(1);
    return kg;
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "weightInKg") {
      setEditData({
        ...editData,
        weightInKg: value,
        pound: value ? (parseFloat(value) * 2.20462).toFixed(1) : '',
        jin: value ? (parseFloat(value) * 2).toFixed(1) : '',
      });
    } else if (name === "pound") {
      const kg = value ? (parseFloat(value) / 2.20462).toFixed(2) : '';
      setEditData({
        ...editData,
        pound: value,
        weightInKg: kg,
        jin: kg ? (parseFloat(kg) * 2).toFixed(1) : '',
      });
    } else if (name === "jin") {
      const kg = value ? (parseFloat(value) / 2).toFixed(2) : '';
      setEditData({
        ...editData,
        jin: value,
        weightInKg: kg,
        pound: kg ? (parseFloat(kg) * 2.20462).toFixed(1) : '',
      });
    } else {
      setEditData({ ...editData, [name]: value });
    }
  };

  useEffect(() => {
    if (open && user?.userId) fetchRecords();
  }, [open, user?.userId]);

  if (!open || !user) return null;

  return (
    <>
      <div className="modal show fade d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1055 }}>
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{user.name} - 體重紀錄</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">

              <div className="row g-2 align-items-end mb-3">
                <div className="col-auto">
                  <label className="form-label">起始日期</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-auto">
                  <label className="form-label">結束日期</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="col-auto">
                  <button className="btn btn-primary" onClick={fetchRecords}>
                    查詢
                  </button>
                </div>
                <div className="col-auto">
                  <label className="form-label">顯示單位</label>
                  <select className="form-select" value={displayUnit} onChange={handleUnitChange}>
                    <option value="kg">公斤 (kg)</option>
                    <option value="lb">磅 (lb)</option>
                    <option value="jin">斤</option>
                  </select>
                </div>
              </div>

              {records.length > 0 && (
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    {/* <LineChart data={records}> */}
                    <LineChart data={[...records].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="recordedAt"
                        tickFormatter={(value) =>
                          new Date(value).toLocaleString("zh-TW", {
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        }
                      />
                      <YAxis />
                      <Tooltip
                        labelFormatter={(value) =>
                          new Date(value).toLocaleString("zh-TW", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        }
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey={(r) => parseFloat(convert(r.weightInKg, displayUnit))}
                        stroke="#3498db"
                        name={`體重 (${displayUnit})`}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>最後編輯</th>
                      <th>時間</th>
                      <th>體重(公斤)</th>
                      <th>磅 (lb)</th>
                      <th>斤</th>
                      <th>備註</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 ? (
                      <tr><td colSpan="5" className="text-center">尚無資料</td></tr>
                    ) : (
                      records.map((r) => (
                        editingId === r.id ? (
                          <tr key={r.id}>
                            <td>{r.sourceName}</td>
                            <td><input className="form-control" type="datetime-local" value={editData.recordedAt} onChange={(e) => setEditData({ ...editData, recordedAt: e.target.value })} /></td>
                            {/* <td>
                              <input className="form-control"
                                value={editData.weightInKg}
                                onChange={(e) => setEditData({ ...editData, weightInKg: e.target.value })} />
                            </td> */}
                            <td>
                              <input
                                className="form-control"
                                name="weightInKg"
                                value={editData.weightInKg}
                                onChange={handleFieldChange}
                              />
                            </td>
                            <td>
                              <input
                                className="form-control"
                                name="pound"
                                value={editData.pound}
                                onChange={handleFieldChange}
                              />
                            </td>
                            <td>
                              <input
                                className="form-control"
                                name="jin"
                                value={editData.jin}
                                onChange={handleFieldChange}
                              />
                            </td>
                            <td><input className="form-control" value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} /></td>
                            <td>
                              <button className="btn btn-success btn-sm me-1" onClick={() => handleSave(r.id)}>儲存</button>
                              <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>取消</button>
                            </td>
                          </tr>
                        ) : (
                          <tr key={r.id}>
                            <td>{r.sourceName}</td>
                            <td>{r.recordedAt?.replace("T", " ").slice(0, 16)}</td>
                            <td>{r.weightInKg}</td>
                            <td>{(r.weightInKg * 2.20462).toFixed(1)}</td>
                            <td>{(r.weightInKg * 2).toFixed(1)}</td>
                            <td>{r.notes}</td>
                            <td>
                              <button className="btn btn-outline-primary btn-sm me-1" onClick={() => handleEdit(r)}>編輯</button>
                              <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(r.id)}>刪除</button>
                            </td>
                          </tr>
                        )
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>關閉</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
    </>
  );
}

export default AdminWeightHistory;
